<template>
  <a-modal
    :open="open"
    width="750px"
    :body-style="{ minHeight: 'min(600px, 70vh)' }"
    :keyboard="false"
    centered
    :mask-closable="false"
    :title="`${props.isEdit ? 'Editar' : 'Crear'} plantilla`"
    @cancel="handleClose"
  >
    <a-tabs v-if="isEdit" v-model:activeKey="currentStep">
      <a-tabs-pane v-for="(step, index) in steps" :key="index" :tab="step" />
    </a-tabs>
    <CustomStepper v-else v-model:value="currentStep" :items="steps" readonly />

    <section class="steps--container">
      <keep-alive>
        <component
          ref="currentStepRef"
          :key="steps[currentStep]"
          :is="StepComponent[currentStep as keyof typeof StepComponent]"
          :content="isEdit ? smsTemplate?.template : createTemplateDto?.content"
          :sms-template="smsTemplate"
          @toggle-button="(disabled) => toggleDisabledButton(disabled)"
        />
      </keep-alive>
    </section>

    <template #footer>
      <a-button @click="handleSecondaryAction"> {{ secondaryButtonText }} </a-button>

      <a-button
        v-if="isEdit"
        type="primary"
        :disabled="isButtonDisabled"
        @click="handleSaveChanges"
      >
        Guardar {{ steps[currentStep] }}
      </a-button>

      <a-button v-else type="primary" :disabled="isButtonDisabled" @click="handlePrimaryAction">
        {{ primaryButtonText }}
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { createVNode, ref, watch, computed } from "vue";
import { useRoute } from "vue-router";
import { Modal, message } from "ant-design-vue";

import { CreateTemplateDto } from "@/@types/channels/sms/dto";
import { templateParamRegex } from "@/app/channels/utils/regex";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { ResponseError } from "@/app/shared/utils/httpManager";
import { smsTemplatesService } from "@/app/channels/services/SmsTemplates.service";
import { SmsTemplate } from "@/@types/channels/sms";

import CustomStepper from "@/app/shared/components/organisms/CustomStepper.vue";
import ConfigurationStep from "@/app/channels/components/modals/sms/templates/steps/ConfigurationStep.vue";
import ContentStep from "@/app/channels/components/modals/sms/templates/steps/ContentStep.vue";
import ParametersStep from "@/app/channels/components/modals/sms/templates/steps/ParametersStep.vue";
import { useStore } from "vuex";

const StepComponent = {
  0: ConfigurationStep,
  1: ContentStep,
  2: ParametersStep,
} satisfies Record<number, unknown>;

const steps = ["Configuración", "Contenido", "Parámetros"];
const CANCEL_ACTION_TEXT = "Cancelar";
const BACK_STEP_ACTION_TEXT = "Volver";
const NEXT_STEP_ACTION_TEXT = "Continuar";
const CREATE_ACTION_TEXT = "Crear";

const props = withDefaults(
  defineProps<{
    open: boolean;
    isEdit: boolean;
    smsTemplate?: SmsTemplate;
    isEmail?: boolean;
  }>(),
  {
    isEmail: false,
  }
);

const emit = defineEmits<{ "update:open": [open: boolean]; update: [] }>();

const currentStepRef =
  ref<InstanceType<typeof ConfigurationStep | typeof ContentStep | typeof ParametersStep>>();
const currentStep = ref(0);
const secondaryButtonText = ref(CANCEL_ACTION_TEXT);
const primaryButtonText = ref(NEXT_STEP_ACTION_TEXT);
const isButtonDisabled = ref(true);

const handleClose = () => emit("update:open", false);

const toggleDisabledButton = (disabled: boolean) => {
  isButtonDisabled.value = disabled;
};

watch(currentStep, (stepValue) => {
  if (stepValue === 0) secondaryButtonText.value = CANCEL_ACTION_TEXT;
  if (stepValue > 0) secondaryButtonText.value = BACK_STEP_ACTION_TEXT;
  if (stepValue === steps.length - 1) primaryButtonText.value = CREATE_ACTION_TEXT;
  if (stepValue < steps.length - 1) primaryButtonText.value = NEXT_STEP_ACTION_TEXT;
});

const handleSecondaryAction = () => {
  console.log("Opcion: ", currentStep.value);
  if (currentStep.value > 0) {
    currentStep.value--;
    toggleDisabledButton(false);
    return;
  }

  handleClose();
};

let createTemplateDto: CreateTemplateDto;

const handlePrimaryAction = () => {
  createTemplateDto = { ...createTemplateDto, ...currentStepRef.value?.formState };
  const isContentStep = currentStep.value === 1;

  const hasParameters = createTemplateDto.content
    ? createTemplateDto.content?.match(templateParamRegex)?.length
    : false;

  if (!hasParameters && isContentStep) {
    handleRequest();
    return;
  }

  if (currentStep.value < steps.length - 1) {
    currentStep.value++;
    toggleDisabledButton(true);
    return;
  }

  handleRequest();
};

const route = useRoute();
const isViewTemplates = computed(() => route.path == "/channels/template");
const store = useStore();

const channelCustomerSms = computed(() => {
  return store.getters["getChannelsCustomersList"].find((el) => el.status == 1);
});

const handleRequest = () => {
  const channelCustomerId = Number(route.params.channel_customer) || channelCustomerSms.value?.id;

  Modal.confirm({
    icon: createVNode(QuestionCircleOutlined),
    title: "¿Estás seguro de crear esta nueva plantilla?",
    okText: "Crear",
    cancelText: "Cancelar",
    centered: true,
    onOk: async () => {
      try {
        await smsTemplatesService.create(createTemplateDto, channelCustomerId);
        emit("update");
        message.success(
          isViewTemplates.value
            ? "Se ha creado la plantilla con éxito"
            : "Se ha solicitado la plantilla"
        );
        handleClose();
      } catch (error) {
        let errorMessage = "Ha ocurrido un error";

        if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

        message.error(errorMessage);
        console.log(error);
      }
      handleClose();
    },
  });
};

const handleSaveChanges = async () => {
  const templateId = props.smsTemplate?.id;

  if (!templateId || !currentStepRef.value) return;

  try {
    await smsTemplatesService.update(templateId, currentStepRef.value.formState);
    emit("update");
    message.success("Se guardaron los cambios");
    handleClose();
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";

    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

    message.error(errorMessage);
    console.log(error);
  }
};
</script>

<style scoped lang="sass">
.steps
  &--container
    margin: 24px 60px
    height: calc(100% - 100px)
</style>
